import { useCallback, useContext, forwardRef, useRef, useState, useEffect, MouseEvent } from 'react'
import { css } from '@emotion/react'
import { Popover } from '@mui/material'
import { debounce, isEqual } from 'lodash'
import { ChevronsUpDown, Search as SearchIcon, TextSearch, X } from 'lucide-react'
import { GroupedVirtuosoHandle, Virtuoso, Components } from 'react-virtuoso'
import { Checkbox } from '@components/formFields/checkbox'
import { ListLoader } from '@components/skeletonLoader/listLoader'
import { Button } from '@components/v2/button'
import { useOverlay } from '@global/hooks/useOverlay'
import { Context } from './context'
import { OverlayProps, FocusDirectionFunction, ISelectProps, MultiSelectProps } from './types'
import { t } from '@lingui/core/macro'

const Overlay = ({ sx = {}, popoverProps, delay = 300, children }: OverlayProps) => {
  const [showContent, setShowContent] = useState(false)
  const { anchorEl, anchorOrigin, transformOrigin, open, onClose, id, ...rest } = popoverProps

  useEffect(() => {
    if (!delay) return

    let timer

    if (open) {
      timer = setTimeout(() => {
        setShowContent(true)
      }, delay)
    } else {
      setShowContent(false)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [open, delay])

  return (
    <Popover
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      open={open}
      onClose={onClose}
      id={id}
      sx={{
        padding: 10,
        '.MuiPaper-root': {
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          boxSizing: 'border-box',
          borderRadius: '8px',
        },
        ...sx,
      }}
      {...rest}
    >
      {showContent || !delay ? children : <ListLoader count={12} height={24} gap={5} />}
    </Popover>
  )
}

const Input = ({ label, placeholder, onChange, onBlur }) => (
  <input
    type="search"
    aria-label={label}
    placeholder={placeholder}
    onChange={onChange}
    onBlur={onBlur}
    className="border-none p-2 flex-1 focus:outline-none"
  />
)

const Search = () => {
  const { onSearch, onSearchBlur } = useContext(Context)

  return (
    <section className="font-sans flex items-center gap-1 py-2 px-4 border-b border-cv-blue-200">
      <SearchIcon size={16} />
      <Input label={t`Search`} placeholder={t`Search`} onChange={onSearch} onBlur={onSearchBlur} />
    </section>
  )
}

const selectVirtuosoStyles = css`
  & > [data-viewport-type='element'] {
    position: relative;
  }

  header {
    position: sticky;
    top: 0;
    z-index: 1;
  }
`

const Scroller: Components['Scroller'] = forwardRef(function Scroller(
  { style, context, children, ...rest },
  ref,
) {
  const { totalCount, hideSearch } = context as { totalCount: number; hideSearch: boolean }

  return (
    <>
      {!hideSearch && <Search />}
      <div style={style} ref={ref} {...rest}>
        {totalCount === 0 ? (
          <div className="flex flex-col gap-6 text-center items-center pt-[30px]">
            <TextSearch size={72} />
            <div className="text-lg font-sans">{t`No results were found.`}</div>
          </div>
        ) : (
          children
        )}
      </div>
    </>
  )
})

const VirtualList = ({ children }) => {
  const { data, setVirtualListRef, hideSearch, compact } = useContext(Context)
  const totalCount = data.length

  return (
    <Virtuoso
      ref={ref => {
        setVirtualListRef(ref)
      }}
      css={selectVirtuosoStyles}
      components={{ Scroller }}
      headerFooterTag="header"
      className={` ${compact ? 'min-h-[160px]' : 'min-h-[340px]'}`}
      totalCount={totalCount}
      data={data}
      itemContent={children}
      context={{ totalCount, hideSearch }}
    />
  )
}

const Option = ({ id, children, index, checkbox = false }) => {
  const { onClose, onSelect, getSelected, data, getVirtualListRef } = useContext(Context)
  const virtualRef = getVirtualListRef()
  const selected = getSelected()
  const isSelected = selected.includes(id)
  const selectedClassnames = isSelected
    ? 'font-bold !bg-cv-alice-blue-50 !text-cv-alice-blue-800 !border-b-cv-alice-blue-200 !border-t-cv-alice-blue-200'
    : ''
  const optionId = `option-${id}`

  const handleKeyDown = event => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        onSelect(id, true)
        break
      case 'Escape':
        event.preventDefault()
        onClose()
        break
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault()
        onOptionFocus(id, event.key === 'ArrowDown' ? 'next' : 'prev')
        break
      case 'Tab':
        event.preventDefault()
        onOptionFocus(id, event.shiftKey ? 'prev' : 'next')
        break
    }
  }

  const onOptionFocus = useCallback<FocusDirectionFunction>(
    (id, direction = null) => {
      const currentIndex = data.findIndex(item => item.id === id)
      let nextIndex

      if (direction === 'next') {
        nextIndex = (currentIndex + 1) % data.length

        const itemsRemainingToScroll = currentIndex < data.length - 1

        if (itemsRemainingToScroll) {
          virtualRef?.scrollIntoView({
            index: nextIndex + 1,
            behavior: 'auto',
          })
        }
      } else if (direction === 'prev') {
        nextIndex = (currentIndex - 1 + data.length) % data.length

        const notReachedFirstItem = currentIndex > 0
        if (notReachedFirstItem) {
          virtualRef?.scrollIntoView({
            index: nextIndex - 1,
            behavior: 'auto',
          })
        }
      } else {
        nextIndex = currentIndex
      }

      // Focus on next option
      const nextOptionID = data[nextIndex]?.id
      if (typeof nextOptionID !== 'undefined') {
        document.getElementById(`option-${nextOptionID}`)?.focus()
      }
    },
    [data],
  )

  // Should not close modal when selecting checkboxes to allow for multiple.
  const onSelectClick = useCallback(
    (event: MouseEvent<HTMLDivElement>, id, shouldClose = true) => {
      event.preventDefault()
      onSelect(id, shouldClose)
    },
    [onSelect],
  )

  return (
    <div
      onClick={e => {
        onSelectClick(e, id, !checkbox)
      }}
      className={`flex items-center text-sm text-cv-blue-600 bg-white hover:bg-cv-alice-blue-50 px-4 py-2 border border-l-0 border-r-0 border-t-transparent border-b-transparent hover:border-b-cv-alice-blue-200 hover:border-t-cv-alice-blue-200 ${selectedClassnames}`}
      role="option"
      id={optionId}
      aria-selected={isSelected}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        onOptionFocus(id)
      }}
      tabIndex={index === 0 ? 0 : -1}
    >
      {checkbox ? (
        <Checkbox
          id="my-id"
          checked={isSelected}
          onChange={e => {
            e.stopPropagation()
            onSelectClick(e, id, false)
          }}
          itemPosition="center"
        >
          <div className="flex flex-col gap-4">
            <Checkbox.Label id="my-id" className="font-semibold">
              {children}
            </Checkbox.Label>
          </div>
        </Checkbox>
      ) : (
        children
      )}
    </div>
  )
}

const isMultiSelect = (props: ISelectProps): props is MultiSelectProps => !!props.multi

/**
 * A Select field component that's searchable, accessible, with keyboard nav support,
 * and follows the React composition pattern.
 */
export const Select = (props: ISelectProps) => {
  const multi = isMultiSelect(props)
  const {
    data,
    isDisabled = false,
    isLoading = false,
    selectedItems = [],
    popoverId,
    name = t`Select`,
    popoverDelay = 300,
    hideSearch = false,
    compact = false,
    onClearCallback,
    icon,
    clearable = true,
    children,
  } = props
  const { anchorEl, onClick, onClose, open, id } = useOverlay()

  const store = useRef(data)
  const virtualListRef = useRef<GroupedVirtuosoHandle | null>(null)
  const [results, setResults] = useState(data)
  const [selected, setSelected] = useState<(string | number)[]>(selectedItems || [])

  const setVirtualListRef = useCallback(node => {
    virtualListRef.current = node
  }, [])

  const getVirtualListRef = useCallback(() => virtualListRef.current, [])

  const onSearch = useCallback(
    debounce(event => {
      const val = event.target.value
      setResults(store.current?.filter(d => d.name.toLowerCase().includes(val.toLowerCase())))
    }, 250),
    [],
  )

  const onSelect = useCallback(
    (id: number, close) => {
      if (multi) {
        setSelected(prev => {
          const isAlreadySelected = prev.includes(id)
          const updateSelected = isAlreadySelected ? prev.filter(x => x !== id) : [...prev, id]
          props.onSelectCallback?.(updateSelected)
          if (close) {
            onClose(null, 'select')
          }
          return updateSelected
        })
        return
      }

      props.onSelectCallback?.(id)
      setSelected([id])
      if (close) {
        onClose(null, 'select')
      }
    },
    [multi, props.onSelectCallback, onClose],
  )

  const focusFirstOption = useCallback(() => {
    if (!results[0]) return

    document.getElementById(`option-${results[0].id}`)?.focus()
  }, [results])

  function clearSelection(e: MouseEvent<SVGSVGElement>) {
    e.stopPropagation()
    setSelected([])
    onClearCallback?.('clear')
  }

  const getSelected = useCallback(() => selected, [selected])

  const onSearchBlur = useCallback(() => {
    focusFirstOption()
  }, [focusFirstOption])

  const toggleSelectedByName = name => {
    const item = data.find(d => d.name === name)

    if (item && multi) {
      setSelected(prevSelected => {
        if (prevSelected.includes(item.id)) {
          // If already selected, remove it
          const updated = prevSelected.filter(id => id !== item.id)
          props.onSelectCallback?.(updated)
          return updated
        }

        const updated = [...prevSelected, item.id]
        props.onSelectCallback?.(updated)
        return updated
      })
    }
  }

  const onRemoveSelectedName = name => {
    toggleSelectedByName(name)
  }

  // Data is derived from parent component, if that updates we need to
  // update the results state.
  useEffect(() => {
    if (isEqual(store.current, data)) return

    store.current = data
    setResults(data)
  }, [data])

  useEffect(() => {
    if (isEqual(selected, selectedItems)) return

    setSelected(selectedItems)
  }, [selectedItems])

  const selectedNames: string[] =
    selected?.length > 0
      ? Object.values(
          data
            .filter(d => selected.includes(d.id))
            .reduce((acc, curr) => ({ ...acc, [curr.id]: curr.name }), {}),
        )
      : []

  return (
    <Context.Provider
      value={{
        data: results,
        onClose: () => {
          onClose(null, '')
        },
        hideSearch,
        compact,
        onSearch,
        onSearchBlur,
        onSelect,
        getSelected,
        setVirtualListRef: ref => {
          setVirtualListRef(ref)
        },
        getVirtualListRef,
      }}
    >
      <section
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-owns={popoverId}
        aria-labelledby={`${popoverId}-label`}
        data-testid={`${popoverId}-select`}
      >
        {multi ? (
          <div className="flex gap-1 flex-row pb-1 flex-wrap">
            {selectedNames?.map(name => (
              <div
                key={name}
                className="flex gap-1 py-1 px-2 items-center bg-cv-blue-100 rounded-[4px] border border-cv-blue-200 text-cv-blue-700 text-[12px]"
              >
                {name}
                <X
                  size={16}
                  onClick={() => {
                    onRemoveSelectedName(name)
                  }}
                  className="cursor-pointer"
                />
              </div>
            ))}
          </div>
        ) : null}
        <Button
          id={`${popoverId}-button`}
          aria-labelledby={`${popoverId}-label ${popoverId}-button`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={popoverId}
          variant="select"
          onClick={onClick}
          className="transition-none"
          disabled={isDisabled}
          isLoading={isLoading}
          layout={false}
          innerElementLayout={false}
        >
          {selected.length === 1 && !multi && selectedNames.length ? (
            <>
              <div className="font-normal max-w-[200px] text-ellipsis whitespace-nowrap overflow-hidden">
                {selectedNames[0]}
              </div>
              {clearable && <X size={16} onClick={clearSelection} />}
              <ChevronsUpDown size={16} />
            </>
          ) : (
            <>
              {icon}
              <div>{name}</div>
              <ChevronsUpDown size={16} />
            </>
          )}
        </Button>
        <Overlay
          popoverProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
            },
            open,
            onClose: (_event, reason) => {
              onClose(null, reason)
            },
            anchorEl,
            id,
            role: 'listbox',
            'aria-labelledby': `${popoverId}-label`,
          }}
          delay={popoverDelay}
          sx={{
            '& .MuiPaper-root': {
              width: compact ? 200 : 350,
              border: '1px solid var(--clvn-blue-200)',
              marginTop: '10px',
              overflowY: 'hidden !important',
            },
          }}
        >
          <div className={`font-sans ${compact ? 'min-h-auto' : 'min-h-48'}`}>{children}</div>
        </Overlay>
      </section>
    </Context.Provider>
  )
}

Select.Search = Search
Select.VirtualList = VirtualList
Select.Option = Option
Select.Checkbox = Checkbox
